import { useState } from 'react'
import {
  Brain,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
  Check,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  Paperclip,
} from 'lucide-react'
import Markdown from 'react-markdown'
import type { UIBlock } from '@/hooks/use-agent-chat'
import { cn } from '@/lib/utils'

function formatBytes(n: number | undefined): string {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

export function TextBlock({ text }: { text: string }) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none break-words prose-headings:mb-2 prose-headings:mt-4 prose-headings:text-foreground prose-h2:text-base prose-h3:text-sm prose-p:my-2 prose-p:leading-6 prose-a:text-usa-blue prose-a:underline-offset-2 prose-strong:text-foreground prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-code:rounded prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none">
      <Markdown>{text}</Markdown>
    </div>
  )
}

export function ThinkingBlock({ text, streaming }: { text: string; streaming?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="my-1 rounded-md border bg-muted/40 text-xs">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Brain className={cn('h-3 w-3', streaming && 'animate-pulse')} />
        <span>Thinking{streaming ? '…' : ''}</span>
      </button>
      {open && text ? (
        <div className="px-6 pb-2 font-mono text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap">
          {text}
        </div>
      ) : null}
    </div>
  )
}

export function ToolUseBlock({
  name,
  label,
  input_preview,
  status,
  summary,
}: Extract<UIBlock, { type: 'tool_use' }>) {
  const [open, setOpen] = useState(false)
  const StatusIcon = status === 'running' ? Loader2 : status === 'error' ? AlertCircle : Check
  return (
    <div className="my-1 rounded-md border bg-muted/30 text-xs">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Wrench className="h-3 w-3" />
        <span className="flex-1 truncate text-left">{label || name}</span>
        <StatusIcon
          className={cn(
            'h-3 w-3',
            status === 'running' && 'animate-spin text-muted-foreground',
            status === 'ok' && 'text-emerald-500',
            status === 'error' && 'text-destructive',
          )}
        />
      </button>
      {open ? (
        <div className="space-y-1 px-6 pb-2">
          <div className="font-mono text-[11px] text-muted-foreground">{name}</div>
          {input_preview ? (
            <pre className="whitespace-pre-wrap break-all rounded bg-background/60 p-1.5 font-mono text-[10px] text-muted-foreground">
              {input_preview}
            </pre>
          ) : null}
          {summary ? <div className="text-[11px] text-foreground/80">{summary}</div> : null}
        </div>
      ) : null}
    </div>
  )
}

export function AttachmentBlock({
  filename,
  content_type,
  size_bytes,
  is_image,
  is_pdf,
}: Extract<UIBlock, { type: 'attachment' }>) {
  const Icon = is_image ? ImageIcon : is_pdf ? FileText : Paperclip
  const sizeLabel = formatBytes(size_bytes)
  return (
    <div className="my-1 inline-flex max-w-full items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-xs">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium" title={filename}>
        {filename}
      </span>
      {sizeLabel ? <span className="text-[10px] text-muted-foreground">{sizeLabel}</span> : null}
      {content_type ? (
        <span className="text-[10px] text-muted-foreground">· {content_type}</span>
      ) : null}
    </div>
  )
}

export function MessageBlockRenderer({ block }: { block: UIBlock }) {
  if (block.type === 'text') return <TextBlock text={block.text} />
  if (block.type === 'thinking') return <ThinkingBlock text={block.text} streaming={!block.open} />
  if (block.type === 'tool_use') return <ToolUseBlock {...block} />
  if (block.type === 'attachment') return <AttachmentBlock {...block} />
  return null
}
